import { Component, EventEmitter, forwardRef, Input, Output, OnChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'atom-input-checkbox',
  templateUrl: './input-checkbox.component.html',
  styleUrls: ['./input-checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCheckboxComponent),
      multi: true
    }
  ]
})
export class InputCheckboxComponent implements OnChanges {
  @Input() title: string = '';
  @Input() optionsList: { title: string, value: string }[] = [];
  @Input() disabled: boolean = false;
  @Input() isMultiple: boolean = false;
  @Input() placeholder: string = '';
  @Output() changed = new EventEmitter<{ value: string, checked: boolean }>();

  selectedValue: { value: string, checked: boolean } = { value: '', checked: false };
  checkedStates: { [key: string]: boolean } = {};

  constructor() {}

  ngOnInit() {
    // Inicializar el estado de los checkboxes
    this.initializeCheckboxStates();
  }

  ngOnChanges() {
    // Reinicializar cuando cambie optionsList (importante para traducciones)
    if (this.optionsList && this.optionsList.length > 0) {
      this.initializeCheckboxStates();
    }
  }

  private initializeCheckboxStates() {
    // Limpiar estados anteriores
    this.checkedStates = {};
    
    // Inicializar todos los checkboxes como no marcados
    this.optionsList.forEach(option => {
      this.checkedStates[option.value] = false;
    });
    
    // Si había un valor seleccionado, mantenerlo si existe en las nuevas opciones
    if (this.selectedValue && this.selectedValue.value && this.selectedValue.checked) {
      const existsInOptions = this.optionsList.some(option => option.value === this.selectedValue.value);
      if (existsInOptions) {
        this.checkedStates[this.selectedValue.value] = true;
      } else {
        // Si el valor seleccionado ya no existe, limpiarlo
        this.selectedValue = { value: '', checked: false };
      }
    }
  }

  reset(): void {
    // Resetear el valor seleccionado
    this.selectedValue = { value: '', checked: false };
    
    // Resetear todos los estados de checkbox
    Object.keys(this.checkedStates).forEach(key => {
      this.checkedStates[key] = false;
    });

    // Emitir el cambio
    this.propagateChange(this.selectedValue);
    this.changed.emit(this.selectedValue);
    this.propagateTouched();
  }

  private propagateChange: (value: { value: string, checked: boolean }) => void = () => {};
  private propagateTouched: () => void = () => {};

  writeValue(value: { value: string, checked: boolean }): void {
    this.selectedValue = value || { value: '', checked: false };
    
    // Resetear todos los checkboxes
    Object.keys(this.checkedStates).forEach(key => {
      this.checkedStates[key] = false;
    });
    
    // Marcar solo el checkbox correspondiente si hay un valor válido
    if (this.selectedValue && this.selectedValue.value && this.selectedValue.checked) {
      this.checkedStates[this.selectedValue.value] = true;
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  onInput(event: MatCheckboxChange): void {
    const value = { value: event.source.value, checked: event.checked };

    // Si no es múltiple (single selection)
    if (!this.isMultiple) {
      if (event.checked) {
        // Si se marca un checkbox, desmarcar todos los demás
        Object.keys(this.checkedStates).forEach(key => {
          this.checkedStates[key] = key === event.source.value;
        });
        this.selectedValue = value;
      } else {
        // Si se desmarca el checkbox actual, limpiar toda la selección
        Object.keys(this.checkedStates).forEach(key => {
          this.checkedStates[key] = false;
        });
        this.selectedValue = { value: '', checked: false };
      }
    } else {
      // Si es múltiple, solo actualizar el checkbox actual
      this.checkedStates[event.source.value] = event.checked;
      this.selectedValue = value;
    }

    this.propagateChange(this.selectedValue);
    this.changed.emit(this.selectedValue);
  }

  onBlur() {
    this.propagateTouched();
  }

  isChecked(value: string): boolean {
    return this.checkedStates[value] || false;
  }
}